/**
 * Detects a category-based guidebook from lang keys.
 *
 * Real Bedrock guidebooks (e.g. Pod's Farm) express their structure entirely in
 * lang keys under `<ns>.guide.*`:
 *
 *   <ns>.guide.main.title / .body            → the homepage
 *   <ns>.guide.<cat>.title / .body           → a category
 *   <ns>.guide.<cat>.<entry>.title / .body   → a named entry ("item")
 *   <ns>.guide.<cat>.<n>                      → a legacy numbered entry
 *                                              (even n = heading, odd n = body)
 *
 * This module reads that hierarchy so the importer can find the categories and
 * the items under them, which the old flat-page importer could not.
 */

export interface DetectedEntry {
  /** A suggested bare name-part id (may be refined by the caller). */
  suggestedId: string;
  titleKey: string;
  bodyKey?: string;
}

export interface DetectedCategory {
  id: string;
  titleKey?: string;
  bodyKey?: string;
  entries: DetectedEntry[];
}

export interface DetectedGuide {
  namespace: string;
  homeTitleKey?: string;
  homeBodyKey?: string;
  categories: DetectedCategory[];
}

const FIELD = new Set(["title", "body"]);

/**
 * Find the namespace that owns a `<ns>.guide.*` hierarchy. Picks the root that
 * appears on the most `.guide.` keys (ignoring the `.ui.guide.` sub-tree).
 */
export function detectGuideNamespace(keys: string[]): string | undefined {
  const counts = new Map<string, number>();
  for (const key of keys) {
    const m = key.match(/^([a-z][a-z0-9_]*)\.guide\./);
    if (!m) continue;
    if (key.startsWith(`${m[1]}.ui.`)) continue;
    counts.set(m[1], (counts.get(m[1]) ?? 0) + 1);
  }
  let best: string | undefined;
  let bestN = 0;
  for (const [ns, n] of counts) {
    if (n > bestN) {
      best = ns;
      bestN = n;
    }
  }
  return best;
}

export function detectGuide(namespace: string, keys: string[]): DetectedGuide {
  const prefix = `${namespace}.guide.`;

  interface CatAcc {
    titleKey?: string;
    bodyKey?: string;
    named: Map<string, { titleKey?: string; bodyKey?: string }>;
    numbered: Set<number>;
  }
  const cats = new Map<string, CatAcc>();
  const result: DetectedGuide = { namespace, categories: [] };

  const catAcc = (id: string): CatAcc => {
    let acc = cats.get(id);
    if (!acc) {
      acc = { named: new Map(), numbered: new Set() };
      cats.set(id, acc);
    }
    return acc;
  };

  // Iterate in file order so category/entry order follows first appearance.
  for (const key of keys) {
    if (!key.startsWith(prefix)) continue;
    const rest = key.slice(prefix.length);
    const parts = rest.split(".");
    const cat = parts[0];
    if (!cat) continue;

    if (cat === "main") {
      if (parts.length === 2 && parts[1] === "title") result.homeTitleKey = key;
      if (parts.length === 2 && parts[1] === "body") result.homeBodyKey = key;
      continue;
    }

    if (parts.length === 2 && FIELD.has(parts[1])) {
      const acc = catAcc(cat);
      if (parts[1] === "title") acc.titleKey = key;
      else acc.bodyKey = key;
    } else if (parts.length === 2 && /^\d+$/.test(parts[1])) {
      catAcc(cat).numbered.add(Number(parts[1]));
    } else if (parts.length === 3 && FIELD.has(parts[2])) {
      const acc = catAcc(cat);
      const entry = acc.named.get(parts[1]) ?? {};
      if (parts[2] === "title") entry.titleKey = key;
      else entry.bodyKey = key;
      acc.named.set(parts[1], entry);
    }
    // Anything else (e.g. changelog versions `<cat>.1.2.1`) is ignored.
  }

  for (const [id, acc] of cats) {
    const entries: DetectedEntry[] = [];

    // Named entries (require a title).
    for (const [entryId, keys2] of acc.named) {
      if (!keys2.titleKey) continue;
      entries.push({ suggestedId: entryId, titleKey: keys2.titleKey, bodyKey: keys2.bodyKey });
    }

    // Legacy numbered entries: even index = heading, next odd index = body.
    // Many packs ship BOTH the named format and a legacy numbered mirror of the
    // same content, so only fall back to numbered when there are no named entries,
    // otherwise every entry would be duplicated.
    if (entries.length === 0) {
      const evens = [...acc.numbered].filter((n) => n % 2 === 0).sort((a, b) => a - b);
      for (const even of evens) {
        const titleKey = `${prefix}${id}.${even}`;
        const bodyKey = acc.numbered.has(even + 1) ? `${prefix}${id}.${even + 1}` : undefined;
        entries.push({ suggestedId: `entry_${even / 2}`, titleKey, bodyKey });
      }
    }

    if (entries.length === 0) continue; // skip title-only groups (changelog, translate…)
    result.categories.push({ id, titleKey: acc.titleKey, bodyKey: acc.bodyKey, entries });
  }

  return result;
}
